// apps/web/src/components/shell/app-shell.tsx — Fase 06, Plano 05 (D-04..D-08, D-16); 17.8 Plano 09 (D-16, D-19), Plano 12 (D-13),
// Plano 22 (D-19: TransicaoPagina por rota), Plano 15 (D-18, D-25: paleta Ctrl+K, folha de atalhos "?" e páginas recentes por usuário em cookie).
import { useCallback, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import type { SessaoAtualResponse } from '@pieltec/contracts';
import { obterSessaoAtual } from '../../lib/session';
import { redirecionamentoDeEntrada } from '../../lib/destino-inicial';
import { deveAbrirFolha } from '../../lib/busca/paleta';
import { registrarRecente } from '../../lib/prefs-ui';
import { SidebarProvider } from '../ui/sidebar';
import { Skeleton } from '../ui/skeleton';
import { AoVivoProvider } from './ao-vivo-provider';
import { AppSidebar } from './app-sidebar';
import { FolhaAtalhos } from './folha-atalhos';
import { PaletaComandos } from './paleta-comandos';
import { ShellTopbar } from './shell-topbar';
import { ShellTopbarSlotProvider } from './topbar-slot';
import { TransicaoPagina } from './transicao-pagina';
import { modoTransicao } from '../../lib/shell/transicao';
import '../../styles/shell.css';
import '../../styles/shell-premium.css';

export interface AppShellProps {
  readonly children: ReactNode;
  /** Rota atual, fornecida pelo router (navegação client-side). Atualiza o
   *  destaque da sidebar/topbar sem remontar o shell. */
  readonly pathname: string;
}

// Estado da checagem de sessão. Enquanto não confirmamos autenticação, NÃO
// renderizamos o conteúdo protegido (evita o "flash" do dashboard antes do
// redirect quando a sessão é inválida — ver 06-REVIEW WR-01).
type EstadoSessao = 'carregando' | 'autenticado' | 'nao-autenticado';

export function AppShell({ children, pathname }: AppShellProps): ReactElement {
  const [usuario, setUsuario] = useState<SessaoAtualResponse | null>(null);
  const [estado, setEstado] = useState<EstadoSessao>('carregando');
  const [densidade, setDensidade] = useState<'compact' | 'comfortable'>('compact');

  useEffect(() => {
    const contextoTablet = window.matchMedia('(pointer: coarse), (max-width: 1023px), (max-width: 1279px) and (max-height: 1023px)');
    const atualizar = () => setDensidade(contextoTablet.matches ? 'comfortable' : 'compact');
    atualizar();
    contextoTablet.addEventListener('change', atualizar);
    return () => contextoTablet.removeEventListener('change', atualizar);
  }, []);

  // Checagem de sessão UMA vez (o shell permanece montado entre navegações
  // client-side, então isto não repete a cada troca de menu).
  useEffect(() => {
    let active = true;
    void obterSessaoAtual().then((res) => {
      if (!active) return;
      if (res.autenticado) {
        setUsuario(res.usuario);
        setEstado('autenticado');
      } else {
        setEstado('nao-autenticado');
        if (typeof window !== 'undefined') {
          window.location.assign('/login');
        }
      }
    });

    return () => {
      active = false;
    };
  }, []);

  // Paleta Ctrl+K (D-18): o estado vive aqui para o atalho global e o botão da sidebar abrirem a mesma paleta; o elemento com o foco
  // antes de abrir é guardado e recebe o foco de volta ao fechar.
  const [paletaAberta, setPaletaAberta] = useState(false);
  const [folhaAberta, setFolhaAberta] = useState(false);
  const elementoAnterior = useRef<HTMLElement | null>(null);
  const abrirPaleta = useCallback((): void => {
    // Já aberta: o foco atual é o campo da paleta, que não deve virar o "elemento anterior".
    if (!paletaAberta) {
      const ativo = document.activeElement;
      elementoAnterior.current = ativo instanceof HTMLElement && ativo !== document.body ? ativo : null;
    }
    setFolhaAberta(false);
    setPaletaAberta(true);
  }, [paletaAberta]);
  const devolverFoco = useCallback((): void => {
    const anterior = elementoAnterior.current;
    elementoAnterior.current = null;
    if (anterior?.isConnected) anterior.focus();
  }, []);

  // Ctrl+K e Cmd+K abrem a paleta e "?" abre a folha de atalhos (fora de campo editável e sem outra janela aberta), em toda tela de
  // gestão (o coletor não usa este shell).
  const autenticado = estado === 'autenticado';
  useEffect(() => {
    if (!autenticado || pathname.startsWith('/coletor')) return undefined;
    const aoTeclar = (evento: KeyboardEvent): void => {
      if ((evento.ctrlKey || evento.metaKey) && !evento.altKey && !evento.shiftKey && evento.key.toLowerCase() === 'k') {
        evento.preventDefault();
        abrirPaleta();
        return;
      }
      if (evento.ctrlKey || evento.metaKey || evento.altKey || document.querySelector('[role="dialog"]') !== null) return;
      const alvo = evento.target instanceof HTMLElement ? evento.target : document.body;
      if (deveAbrirFolha({ key: evento.key, alvo })) {
        evento.preventDefault();
        setFolhaAberta(true);
      }
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [autenticado, pathname, abrirPaleta]);

  // Páginas recentes (D-25): a cada navegação grava o caminho no cookie do usuário (sem Web Storage); a leitura filtra por permissão.
  const userId = usuario?.userId;
  useEffect(() => {
    if (!autenticado || userId === undefined || pathname === '/login') return;
    registrarRecente(userId, pathname);
  }, [autenticado, userId, pathname]);

  // Raiz ou Painel sem permissão: troca a URL pela página inicial do perfil (sem recarregar).
  // `redirecionamentoDeEntrada` nunca devolve o próprio pathname, então não há laço.
  const redirecionarPara = estado === 'autenticado' && usuario ? redirecionamentoDeEntrada(pathname, usuario.permissions) : null;
  useEffect(() => {
    if (!redirecionarPara) return;
    window.history.replaceState(null, '', redirecionarPara);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, [redirecionarPara]);

  // Enquanto carrega (ou já decidiu redirecionar), mostramos apenas o esqueleto do layout (D-19):
  // o conteúdo protegido nunca aparece e não há ícone girando.
  if (estado !== 'autenticado' || redirecionarPara) {
    return (
      <div role="status" aria-live="polite" className="shell-esqueleto">
        <span className="sr-only">
          {estado === 'carregando' ? 'Verificando sessão…' : redirecionarPara ? 'Abrindo sua página inicial…' : 'Redirecionando para o login…'}
        </span>
        <div className="shell-esqueleto__faixa" aria-hidden="true">
          <Skeleton className="shell-esqueleto__marca" />
          <Skeleton className="shell-esqueleto__item" />
          <Skeleton className="shell-esqueleto__item" />
          <Skeleton className="shell-esqueleto__item" />
        </div>
        <div className="shell-esqueleto__area" aria-hidden="true">
          <div className="shell-esqueleto__topbar">
            <Skeleton className="shell-esqueleto__botao" />
            <Skeleton className="shell-esqueleto__trilha" />
          </div>
          <div className="shell-esqueleto__conteudo">
            <Skeleton className="shell-esqueleto__bloco" />
            <Skeleton className="shell-esqueleto__bloco" />
            <Skeleton className="shell-esqueleto__bloco" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider defaultOpen={true} pathname={pathname}>
      <AoVivoProvider permissoes={usuario?.permissions ?? []}>
        <ShellTopbarSlotProvider>
          {/* Skip-link para navegação por teclado acessível (ADR-005 D9) */}
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:outline-none"
          >
            Pular para o conteúdo principal
          </a>

          <div className="workspace-shell flex min-h-screen w-full bg-page text-foreground" data-density={densidade}>
            {/* Sidebar com os domínios filtrados por permissão (D-04..D-08, D-16) */}
            <AppSidebar usuario={usuario} pathname={pathname} onAbrirBusca={abrirPaleta} onAbrirAtalhos={() => setFolhaAberta(true)} />

            {/* Área principal: topbar de 52px e canvas (D-16) */}
            <div className="flex flex-1 flex-col min-w-0">
              <ShellTopbar pathname={pathname} />

              <main id="main-content" className="workspace-main flex-1 bg-page text-foreground" tabIndex={-1}>
                <div className="workspace-canvas mx-auto w-full">
                  <TransicaoPagina chave={pathname} modo={modoTransicao(pathname)}>{children}</TransicaoPagina>
                </div>
              </main>
            </div>
          </div>

          <PaletaComandos
            aberto={paletaAberta} onAbertoChange={setPaletaAberta} permissoes={usuario?.permissions ?? []}
            userId={usuario?.userId ?? ''} aoDevolverFoco={devolverFoco}
          />
          <FolhaAtalhos aberto={folhaAberta} onAbertoChange={setFolhaAberta} />
        </ShellTopbarSlotProvider>
      </AoVivoProvider>
    </SidebarProvider>
  );
}
